import { useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";
import { useFormInternalContext } from "../../visual/FormProvider/formContext.js";
import { createFieldApi } from "./createFieldApi.js";
const emptyPlaceholder = {};
function useFormField(fieldName, maybeLazyOptions) {
  const { api: formApi } = useFormInternalContext();
  const rOptionsInitialized = useRef(false);
  const rOptions = useRef(
    rOptionsInitialized.current ? (
      // when already initialized a placeholder empty option object is used
      emptyPlaceholder
    ) : (
      // the first render, when possible, initialize the ref value
      (typeof maybeLazyOptions === "function" ? maybeLazyOptions() : maybeLazyOptions) ?? emptyPlaceholder
    )
  );
  rOptionsInitialized.current = true;
  useLayoutEffect(() => {
    if (typeof maybeLazyOptions === "function") {
      if (Object.keys(rOptions.current).length === 0) {
        rOptions.current = maybeLazyOptions();
        forceRender();
      }
    } else {
      const replacer = (_key, val) => typeof val === "function" ? val.toString() : val;
      const changed = JSON.stringify(maybeLazyOptions, replacer) !== JSON.stringify(rOptions.current, replacer);
      if (changed) {
        rOptions.current = maybeLazyOptions ?? rOptions.current;
        forceRender();
      }
    }
  }, [maybeLazyOptions]);
  const { mutation, transform, validations, hidden, disabled, required, type, initialValue } = rOptions.current;
  const rCreateFieldInitialConfig = useRef({
    disabled: !!disabled,
    required: !!required,
    visible: !hidden,
    initialValue,
    // @ts-expect-error // FIXME: we need a better way to narrow the type definition for "type"
    type
  });
  useLayoutEffect(() => {
    rCreateFieldInitialConfig.current = {
      disabled: !!disabled,
      required: !!required,
      visible: !hidden,
      initialValue,
      // @ts-expect-error // FIXME: we need a better way to narrow the type definition for "type"
      type
    };
  });
  const [fieldApi, setFieldApi] = useState(() => {
    return createFieldApi(formApi, fieldName, rCreateFieldInitialConfig.current);
  });
  useEffect(() => {
    let effectCleared = false;
    const { fieldsOfInterest } = rOptions.current;
    const unsubscribe = formApi.subscribeToField(fieldName, (field, errors, _formApi) => {
      if (effectCleared) return;
      const changedFields = /* @__PURE__ */ new Set();
      if (fieldsOfInterest?.length) {
        for (const key of Object.keys(field)) {
          const fieldKey = key;
          if (field[fieldKey] !== rState.current.field[fieldKey]) {
            changedFields.add(fieldKey);
          }
        }
      }
      rState.current.field = field;
      rState.current.errors = errors;
      if (!fieldsOfInterest?.length) {
        forceRender();
        return;
      }
      if (fieldsOfInterest.some((f) => changedFields.has(f))) {
        forceRender();
      }
    });
    return () => {
      effectCleared = true;
      unsubscribe();
    };
  }, [formApi, fieldName]);
  const [, forceRender] = useReducer((s) => s + 1, 0);
  function getState(targetField) {
    let field = formApi.getField(targetField);
    if (!field) {
      setFieldApi(createFieldApi(formApi, targetField, rCreateFieldInitialConfig.current));
    }
    field = formApi.getField(targetField);
    if (!field) {
      throw new Error(`[useFormField] Field "${targetField}" not found`);
    }
    return {
      field,
      errors: formApi.getFieldErrors(targetField)
    };
  }
  const rState = useRef(getState(fieldName));
  useLayoutEffect(() => void Object.assign(rState.current, getState(fieldName)), [fieldName]);
  useEffect(() => {
    if (hidden !== void 0) {
      fieldApi.setVisible(!hidden);
    }
  }, [fieldApi, hidden]);
  useEffect(() => {
    if (required !== void 0) {
      fieldApi.setRequired(required);
    }
  }, [fieldApi, required]);
  useEffect(() => {
    if (disabled !== void 0) {
      fieldApi.setDisabled(disabled);
    }
  }, [fieldApi, disabled]);
  useEffect(() => {
    if (mutation) {
      fieldApi.addMutation(mutation);
    }
    return () => {
      if (mutation) {
        fieldApi.removeMutation();
      }
    };
  }, [fieldApi, mutation]);
  useEffect(() => {
    if (transform) {
      fieldApi.addTransform(transform);
    }
    return () => {
      if (transform) {
        fieldApi.removeTransform();
      }
    };
  }, [fieldApi, transform]);
  useEffect(() => {
    if (validations) {
      for (const [validationID, validator] of Object.entries(validations)) {
        fieldApi.addValidation(validationID, validator);
      }
    }
    return () => {
      if (validations) {
        for (const validationID of Object.keys(validations)) {
          fieldApi.removeValidation(validationID);
        }
      }
    };
  }, [fieldApi, validations]);
  return { field: rState.current.field, errors: rState.current.errors, fieldApi, formApi };
}
export {
  useFormField
};
//# sourceMappingURL=useFormField.js.map
